/**
 * 요약 탭 컴포넌트 (Feature 113: 모바일 요약 탭 UI 개선)
 * 설비 정보를 표시하고 편집합니다.
 * - 7개 필드: 점검번호(readonly), 관리번호, 점검자, 점검일자, 설치년도, 설치위치, 제조사
 * - 필드 클릭 시 수정 모달 표시, 확인 버튼으로 저장
 */

import { useState, useEffect, useRef, useMemo, ChangeEvent } from "react";
import { MACHINE_TITLES, type MachineKey } from "@ict/shared/constants";
import { validateMachineInfoField, MACHINE_INFO_MAX_LENGTHS } from "@/lib/validation/machineInfo";

// ─────────────────────────────────────────────────────────────
// 아이콘 컴포넌트
// ─────────────────────────────────────────────────────────────

function HashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
      />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function WrenchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// 타입 및 상수
// ─────────────────────────────────────────────────────────────

/** MachineDocument에서 편집 가능한 필드 키 타입 */
type MachineDocumentEditableKey =
  | "modelNo"
  | "checkName"
  | "checkStartDate"
  | "checkEndDate"
  | "modelYear"
  | "position"
  | "modelCreator";

/** FieldDefinition의 키 타입 (가상 필드 포함) */
type FieldKey = MachineDocumentEditableKey | "inspectionNumber";

/**
 * SummaryTab에서 사용하는 설비 정보 타입
 * MachineDocument의 부분집합으로, 타입 안전성을 위해 별도 정의
 */
export interface SummaryTabMachineInfo {
  modelNo?: string;
  checkName?: string;
  checkStartDate?: string;
  checkEndDate?: string;
  modelYear?: string;
  position?: string;
  modelCreator?: string;
}

interface FieldDefinition {
  key: FieldKey;
  label: string;
  type: "readonly" | "text" | "date" | "dateRange";
  placeholder?: string;
  icon: React.ReactNode;
  section: "identity" | "inspection" | "equipment";
  /** dateRange 타입인 경우 종료 키 */
  endKey?: MachineDocumentEditableKey;
}

const FIELD_DEFINITIONS: FieldDefinition[] = [
  {
    key: "inspectionNumber",
    label: "점검번호",
    type: "readonly",
    icon: <HashIcon className="w-4 h-4" />,
    section: "identity",
  },
  {
    key: "modelNo",
    label: "관리번호",
    type: "text",
    placeholder: "관리번호 입력",
    icon: <TagIcon className="w-4 h-4" />,
    section: "identity",
  },
  {
    key: "checkName",
    label: "점검자",
    type: "text",
    placeholder: "점검자명 입력",
    icon: <UserIcon className="w-4 h-4" />,
    section: "inspection",
  },
  {
    key: "checkStartDate",
    label: "점검일자",
    type: "dateRange",
    endKey: "checkEndDate",
    icon: <CalendarIcon className="w-4 h-4" />,
    section: "inspection",
  },
  {
    key: "modelYear",
    label: "설치년도",
    type: "date",
    icon: <WrenchIcon className="w-4 h-4" />,
    section: "equipment",
  },
  {
    key: "position",
    label: "설치위치",
    type: "text",
    placeholder: "설치위치 입력",
    icon: <MapPinIcon className="w-4 h-4" />,
    section: "equipment",
  },
  {
    key: "modelCreator",
    label: "제조사",
    type: "text",
    placeholder: "제조사 입력",
    icon: <BuildingIcon className="w-4 h-4" />,
    section: "equipment",
  },
];

const SECTIONS = [
  { key: "identity", label: "식별 정보", color: "from-cyan-500 to-teal-500" },
  { key: "inspection", label: "점검 정보", color: "from-blue-500 to-indigo-500" },
  { key: "equipment", label: "설비 정보", color: "from-violet-500 to-purple-500" },
] as const;

interface SummaryTabProps {
  /** 설비 정보 (SummaryTabMachineInfo 타입 사용 권장) */
  machineDocument: SummaryTabMachineInfo | null;
  docKey: string;
  machineIndex: number;
  onFieldChange: (field: string, value: string | undefined) => void;
  /** 저장 트리거. immediateChange: state 반영 전 즉시 저장할 변경 사항 (단일 또는 배열) */
  onBlur: (
    immediateChange?: { field: string; value: string } | { field: string; value: string }[]
  ) => void;
  isLoading?: boolean;
  isSaving?: boolean;
  saveError?: string | null;
}

interface EditModalState {
  isOpen: boolean;
  field: FieldDefinition | null;
  value: string;
  endValue?: string; // dateRange용
}

// ─────────────────────────────────────────────────────────────
// 유틸리티 함수
// ─────────────────────────────────────────────────────────────

function generateInspectionNumber(docKey: string, index: number): string {
  const machineTitle = MACHINE_TITLES[docKey as MachineKey] || docKey;
  const paddedIndex = String(index + 1).padStart(3, "0");
  return `${machineTitle}#${paddedIndex}`;
}

function formatDateForInput(dateStr: string | undefined): string {
  if (!dateStr) return "";
  if (dateStr.includes("T")) {
    return dateStr.split("T")[0];
  }
  return dateStr;
}

function formatDateForDisplay(dateStr: string | undefined): string {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
}

// ─────────────────────────────────────────────────────────────
// 섹션 헤더
// ─────────────────────────────────────────────────────────────

function SectionHeader({ label, gradientClass }: { label: string; gradientClass: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-slate-50">
      <div className={`w-1 h-4 rounded-full bg-gradient-to-b ${gradientClass}`} />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 필드 표시 컴포넌트 (클릭 가능)
// ─────────────────────────────────────────────────────────────

function FieldDisplay({
  field,
  displayValue,
  isReadonly,
  onClick,
}: {
  field: FieldDefinition;
  displayValue: string;
  isReadonly: boolean;
  onClick: () => void;
}) {
  // displayValue가 "-"가 아니면 값이 있는 것으로 판단 (dateRange에서도 정확히 동작)
  const hasValue = displayValue !== "-";

  return (
    <button
      type="button"
      onClick={isReadonly ? undefined : onClick}
      disabled={isReadonly}
      className={`
        w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
        ${isReadonly ? "bg-white cursor-default" : "bg-white active:bg-slate-50"}
      `}
    >
      <span className="text-slate-400 flex-shrink-0">{field.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-medium text-slate-400 mb-0.5">{field.label}</div>
        <div className={`text-sm truncate ${hasValue ? "text-slate-700" : "text-slate-300"}`}>
          {displayValue}
        </div>
      </div>
      {!isReadonly && <ChevronRightIcon className="w-4 h-4 text-slate-300 flex-shrink-0" />}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// 수정 모달 (Bottom Sheet)
// ─────────────────────────────────────────────────────────────

function EditModal({
  state,
  onClose,
  onConfirm,
  onChange,
  onEndChange,
  isSaving,
  validationError,
}: {
  state: EditModalState;
  onClose: () => void;
  onConfirm: () => void;
  onChange: (value: string) => void;
  onEndChange?: (value: string) => void;
  isSaving?: boolean;
  validationError?: string | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // 열기 애니메이션
  useEffect(() => {
    if (state.isOpen) {
      // 마운트 후 다음 프레임에 애니메이션 시작
      setIsAnimating(true);
      const openTimer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
      return () => cancelAnimationFrame(openTimer);
    } else {
      // 닫기 애니메이션
      setIsVisible(false);
      const closeTimer = setTimeout(() => {
        setIsAnimating(false);
      }, 300); // 애니메이션 duration과 동일
      return () => clearTimeout(closeTimer);
    }
  }, [state.isOpen]);

  // 포커스 처리 - 모달 애니메이션과 동시에 키보드가 올라오도록 빠르게 포커스
  // 날짜 필드는 포커스 생략 (사용자가 직접 터치하여 달력 열기)
  useEffect(() => {
    if (isVisible && inputRef.current) {
      const isDateField = state.field?.type === "date" || state.field?.type === "dateRange";
      if (!isDateField) {
        const timer = setTimeout(() => {
          inputRef.current?.focus({ preventScroll: true });
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, state.field?.type]);

  // 애니메이션 중이 아니고 열려있지도 않으면 렌더링 안함
  if (!isAnimating && !state.isOpen) return null;
  if (!state.field) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleEndChange = (e: ChangeEvent<HTMLInputElement>) => {
    onEndChange?.(e.target.value);
  };

  const renderInput = () => {
    const { field, value, endValue } = state;

    // TypeScript null safety guard
    if (!field) return null;

    if (field.type === "dateRange") {
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">시작일</label>
            <input
              ref={inputRef}
              type="date"
              value={formatDateForInput(value)}
              onChange={handleChange}
              className={`w-full px-4 py-3 text-base rounded-xl border-2 ${validationError ? "border-red-400" : "border-slate-200"} focus:border-blue-500 focus:ring-0 transition-colors`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">종료일</label>
            <input
              type="date"
              value={formatDateForInput(endValue)}
              onChange={handleEndChange}
              min={formatDateForInput(value)}
              className={`w-full px-4 py-3 text-base rounded-xl border-2 ${validationError ? "border-red-400" : "border-slate-200"} focus:border-blue-500 focus:ring-0 transition-colors`}
            />
          </div>
          {validationError && <p className="mt-2 text-sm text-red-500">{validationError}</p>}
        </div>
      );
    }

    if (field.type === "date") {
      return (
        <>
          <input
            ref={inputRef}
            type="date"
            value={formatDateForInput(value)}
            onChange={handleChange}
            className={`w-full px-4 py-3 text-base rounded-xl border-2 ${validationError ? "border-red-400" : "border-slate-200"} focus:border-blue-500 focus:ring-0 transition-colors`}
          />
          {validationError && <p className="mt-2 text-sm text-red-500">{validationError}</p>}
        </>
      );
    }

    return (
      <>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={field.placeholder}
          enterKeyHint="done"
          autoComplete="off"
          maxLength={MACHINE_INFO_MAX_LENGTHS[field.key] || undefined}
          className={`w-full px-4 py-3 text-base rounded-xl border-2 ${validationError ? "border-red-400" : "border-slate-200"} focus:border-blue-500 focus:ring-0 transition-colors placeholder:text-slate-300`}
        />
        {validationError && <p className="mt-2 text-sm text-red-500">{validationError}</p>}
      </>
    );
  };

  return (
    <div
      className={`fixed inset-0 z-50 backdrop-blur-sm transition-all duration-300 ease-out ${
        isVisible ? "bg-black/40" : "bg-black/0"
      }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl transition-transform duration-300 ease-out ${
          isVisible ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ marginBottom: "var(--keyboard-height, 0px)" }}
      >
        {/* 핸들 바 */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>

        {/* 헤더 */}
        <div className="flex items-center justify-between px-4 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="text-blue-500">{state.field.icon}</span>
            <span className="font-semibold text-slate-800">{state.field.label}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 -mr-2 rounded-full hover:bg-slate-100 active:bg-slate-200 transition-colors"
          >
            <XIcon className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* 입력 영역 */}
        <div className="px-4 py-5">{renderInput()}</div>

        {/* 버튼 영역 */}
        <div className="flex gap-3 px-4 pb-8">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 rounded-xl font-medium text-slate-600 bg-slate-100 active:bg-slate-200 transition-colors"
          >
            취소
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSaving}
            className="flex-1 py-3 rounded-xl font-medium text-white bg-blue-500 active:bg-blue-600 disabled:bg-blue-300 transition-colors"
          >
            {isSaving ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                저장 중
              </span>
            ) : (
              "확인"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 로딩 / 빈 상태
// ─────────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="animate-pulse">
      {[1, 2, 3].map((section) => (
        <div key={section}>
          <div className="h-8 bg-slate-100" />
          {[1, 2].map((item) => (
            <div key={item} className="flex items-center gap-3 px-4 py-3">
              <div className="w-4 h-4 bg-slate-200 rounded" />
              <div className="flex-1">
                <div className="w-12 h-3 bg-slate-200 rounded mb-1" />
                <div className="w-24 h-4 bg-slate-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6">
      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <svg
          className="w-7 h-7 text-slate-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <p className="text-sm text-slate-500">설비 정보를 불러올 수 없습니다.</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 상태 바
// ─────────────────────────────────────────────────────────────

function StatusBar({ error }: { error?: string | null }) {
  if (!error) return null;

  return (
    <div className="px-4 py-2 bg-red-50 text-red-600 text-xs font-medium flex items-center gap-2">
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      {error}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 메인 컴포넌트
// ─────────────────────────────────────────────────────────────

export function SummaryTab({
  machineDocument,
  docKey,
  machineIndex,
  onFieldChange,
  onBlur,
  isLoading,
  isSaving,
  saveError,
}: SummaryTabProps) {
  const [editModal, setEditModal] = useState<EditModalState>({
    isOpen: false,
    field: null,
    value: "",
    endValue: "",
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  // 점검번호 계산 (메모이제이션)
  const inspectionNumber = useMemo(
    () => generateInspectionNumber(docKey, machineIndex),
    [docKey, machineIndex]
  );

  // 로딩 중
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // 문서 없음
  if (!machineDocument) {
    return <EmptyState />;
  }

  // 필드 값 가져오기 (타입 안전)
  const getFieldValue = (key: MachineDocumentEditableKey): string => {
    const value = machineDocument[key];
    return typeof value === "string" ? value : "";
  };

  // 필드 클릭 핸들러
  const handleFieldClick = (field: FieldDefinition) => {
    if (field.type === "readonly") return;

    if (field.type === "dateRange" && field.endKey) {
      setEditModal({
        isOpen: true,
        field,
        value: getFieldValue(field.key as MachineDocumentEditableKey),
        endValue: getFieldValue(field.endKey),
      });
    } else {
      setEditModal({
        isOpen: true,
        field,
        value: getFieldValue(field.key as MachineDocumentEditableKey),
      });
    }
  };

  // 모달 닫기
  const handleCloseModal = () => {
    setEditModal({ isOpen: false, field: null, value: "", endValue: "" });
    setValidationError(null);
  };

  // 모달 값 변경
  const handleModalChange = (value: string) => {
    setEditModal((prev) => ({ ...prev, value }));
    setValidationError(null);
  };

  const handleModalEndChange = (value: string) => {
    setEditModal((prev) => ({ ...prev, endValue: value }));
    setValidationError(null);
  };

  // 확인 버튼 클릭 (저장)
  const handleConfirm = () => {
    if (!editModal.field) return;

    // 필드 검증
    const fieldKey = editModal.field.key;
    let error;
    if (editModal.field.type === "dateRange" && editModal.field.endKey) {
      error = validateMachineInfoField(fieldKey, editModal.value, editModal.endValue);
    } else {
      error = validateMachineInfoField(fieldKey, editModal.value);
    }
    if (error) {
      setValidationError(error.message);
      return;
    }
    setValidationError(null);

    // 변경 사항을 수집 (state 반영 전에 onBlur에 직접 전달)
    const changes: { field: string; value: string }[] = [];

    if (editModal.field.type === "dateRange" && editModal.field.endKey) {
      onFieldChange(editModal.field.key, editModal.value);
      onFieldChange(editModal.field.endKey, editModal.endValue);
      changes.push(
        { field: editModal.field.key, value: editModal.value },
        { field: editModal.field.endKey, value: editModal.endValue ?? "" }
      );
    } else {
      onFieldChange(editModal.field.key, editModal.value);
      changes.push({ field: editModal.field.key, value: editModal.value });
    }

    onBlur(changes); // 변경 사항을 직접 전달하여 저장 트리거
    handleCloseModal();
  };

  // 표시 값 포맷팅
  const getDisplayValue = (field: FieldDefinition): string => {
    if (field.key === "inspectionNumber") {
      return inspectionNumber;
    }

    if (field.type === "dateRange" && field.endKey) {
      const start = getFieldValue(field.key as MachineDocumentEditableKey);
      const end = getFieldValue(field.endKey);
      if (!start && !end) return "-";
      return `${formatDateForDisplay(start)} ~ ${formatDateForDisplay(end)}`;
    }

    if (field.type === "date") {
      return formatDateForDisplay(getFieldValue(field.key as MachineDocumentEditableKey));
    }

    return getFieldValue(field.key as MachineDocumentEditableKey) || "-";
  };

  return (
    <div className="flex flex-col bg-slate-50">
      {/* 에러 상태 바 */}
      <StatusBar error={saveError} />

      {/* 필드 목록 */}
      <div className="flex-1">
        {SECTIONS.map((section) => {
          const sectionFields = FIELD_DEFINITIONS.filter((f) => f.section === section.key);
          if (sectionFields.length === 0) return null;

          return (
            <div key={section.key}>
              <SectionHeader label={section.label} gradientClass={section.color} />
              <div className="bg-white divide-y divide-slate-100">
                {sectionFields.map((field) => (
                  <FieldDisplay
                    key={field.key}
                    field={field}
                    displayValue={getDisplayValue(field)}
                    isReadonly={field.type === "readonly"}
                    onClick={() => handleFieldClick(field)}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* 하단 여백 */}
        <div className="h-4" />
      </div>

      {/* 수정 모달 */}
      <EditModal
        state={editModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirm}
        onChange={handleModalChange}
        onEndChange={handleModalEndChange}
        isSaving={isSaving}
        validationError={validationError}
      />
    </div>
  );
}

export default SummaryTab;
